import { useCallback, useEffect, useRef } from 'react';
import { driver, DriveStep, Config } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useI18n } from './useI18n';

const TOUR_COMPLETED_PREFIX = 'onboarding_tour_completed_';

interface TourOptions {
    tourId: string;
    steps: DriveStep[];
    /** If true, show tour automatically on first visit. Default: true */
    autoStart?: boolean;
    /** Delay in ms before auto-starting. Default: 800 */
    autoStartDelay?: number;
    onComplete?: () => void;
    onClose?: () => void;
}

export const useOnboardingTour = () => {
    const { t, isRTL, currentLanguage } = useI18n();
    const driverRef = useRef<ReturnType<typeof driver> | null>(null);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (driverRef.current) {
                driverRef.current.destroy();
                driverRef.current = null;
            }
        };
    }, []);

    const isTourCompleted = useCallback((tourId: string): boolean => {
        return localStorage.getItem(`${TOUR_COMPLETED_PREFIX}${tourId}`) === 'true';
    }, []);

    const markTourCompleted = useCallback((tourId: string) => {
        localStorage.setItem(`${TOUR_COMPLETED_PREFIX}${tourId}`, 'true');
    }, []);

    const resetTour = useCallback((tourId: string) => {
        localStorage.removeItem(`${TOUR_COMPLETED_PREFIX}${tourId}`);
    }, []);

    const startTour = useCallback((options: TourOptions) => {
        const {
            tourId,
            steps,
            autoStart = true,
            autoStartDelay = 800,
            onComplete,
            onClose,
        } = options;

        const shouldAutoStart = autoStart && !isTourCompleted(tourId);

        const config: Config = {
            showProgress: true,
            animate: true,
            smoothScroll: true,
            allowClose: true,
            stagePadding: 8,
            stageRadius: 8,
            popoverClass: `onboarding-popover ${isRTL ? 'onboarding-rtl' : ''}`,
            nextBtnText: t('onboarding.next'),
            prevBtnText: t('onboarding.previous'),
            doneBtnText: t('onboarding.done'),
            progressText: t('onboarding.progressText', { current: '{{current}}', total: '{{total}}' }),
            steps,
            onDestroyStarted: () => {
                markTourCompleted(tourId);
                if (driverRef.current) {
                    driverRef.current.destroy();
                }
                onClose?.();
            },
            onDestroyed: () => {
                markTourCompleted(tourId);
                onComplete?.();
            },
        };

        // Destroy any existing instance
        if (driverRef.current) {
            driverRef.current.destroy();
        }

        const driverInstance = driver(config);
        driverRef.current = driverInstance;

        if (shouldAutoStart) {
            setTimeout(() => {
                driverInstance.drive();
            }, autoStartDelay);
        }

        return driverInstance;
    }, [t, isRTL, currentLanguage, isTourCompleted, markTourCompleted]);

    const manualStart = useCallback((options: TourOptions) => {
        // Destroy any existing instance
        if (driverRef.current) {
            driverRef.current.destroy();
        }

        const config: Config = {
            showProgress: true,
            animate: true,
            smoothScroll: true,
            allowClose: true,
            stagePadding: 8,
            stageRadius: 8,
            popoverClass: `onboarding-popover ${isRTL ? 'onboarding-rtl' : ''}`,
            nextBtnText: t('onboarding.next'),
            prevBtnText: t('onboarding.previous'),
            doneBtnText: t('onboarding.done'),
            progressText: t('onboarding.progressText', { current: '{{current}}', total: '{{total}}' }),
            steps: options.steps,
            onDestroyStarted: () => {
                if (driverRef.current) {
                    driverRef.current.destroy();
                }
                options.onClose?.();
            },
            onDestroyed: () => {
                options.onComplete?.();
            },
        };

        const driverInstance = driver(config);
        driverRef.current = driverInstance;
        driverInstance.drive();
    }, [t, isRTL]);

    return {
        startTour,
        manualStart,
        isTourCompleted,
        resetTour,
        markTourCompleted,
    };
};
